import {
  faBuilding,
  faCalculatorSimple,
  faUser,
  faCreditCard,
} from "@fortawesome/pro-regular-svg-icons";
import { AccessorColumnDef } from "@tanstack/react-table";
import { formatDialect, trino, hive } from "sql-formatter";

import { Pill, PillVariants } from "src/base-components/Pill";
import { Cell, Header } from "src/base-components/Table";
import {
  ColumnMappingOutput,
  FeatureQueryEnvironmentSetting,
  FeatureQueryStatus,
  FeatureResponse,
  PreviewQueryRequestEnvironmentEnum,
} from "src/clients/features-control";
import { formatDuration as formatDurationUtil } from "src/utils/numbers";

export const STATUS_COLOR_MAP: Record<FeatureQueryStatus, PillVariants> = {
  active: "green",
  inactive: "gray",
} as const;

export type Entity = "card" | "account" | "company";
export const ENTITY_ICON_MAP = {
  card: faCreditCard,
  account: faUser,
  company: faBuilding,
} as const;

export type Query = {
  id: string;
  environment_settings: Record<string, FeatureQueryEnvironmentSetting>;
  event: string;
  query: string;
  backfilling_query: string;
  columns: ColumnMappingOutput;
  window_hop: number;
  window_length: number;
  created_at: string;
  updated_at: string;
  finalized_at?: string;
  etag: string;
};

export type QueryRow = {
  id: string;
  column: string;
  window_hop: number;
  window_length: number;
  status: FeatureQueryStatus;
  entities: Entity[];
  event: string;
  query: string;
};

export type Feature = FeatureResponse;

const DAY_IN_SECONDS = 24 * 60 * 60;
export const formatTimeWindow = (seconds: number): string => {
  if (seconds <= DAY_IN_SECONDS) {
    return formatDurationUtil(seconds * 1000, {
      locale: "long",
    });
  }

  return `${Math.floor(seconds / DAY_IN_SECONDS)} days`;
};

export const commonColumns = <T extends object>() =>
  [
    {
      accessorKey: "event",
      header: (info) => <Header info={info}>Event</Header>,
      cell: (info) => (
        <Cell info={info}>
          <Pill size="sm" variant="gray">
            <Pill.Icon icon={faCalculatorSimple} />
            <Pill.Text>{info.getValue() as string}</Pill.Text>
          </Pill>
        </Cell>
      ),
    },
    {
      accessorKey: "window_length",
      header: (info) => (
        <Header info={info}>
          <span className="whitespace-nowrap">Time window</span>
        </Header>
      ),
      cell: (info) => (
        <Cell info={info}>{formatTimeWindow(info.getValue() as number)}</Cell>
      ),
      minSize: 94,
      maxSize: 94,
    },
    {
      accessorKey: "window_hop",
      header: (info) => <Header info={info}>Refresh</Header>,
      cell: (info) => (
        <Cell info={info}>{formatTimeWindow(info.getValue() as number)}</Cell>
      ),
    },
  ] as const satisfies AccessorColumnDef<T>[];

export const formatAthenaSqlQuery = (sql: string | undefined) => {
  if (!sql) return "";

  try {
    return formatDialect(sql, { dialect: trino });
  } catch {
    try {
      return formatDialect(sql, { dialect: hive });
    } catch {
      return sql;
    }
  }
};

export type OutletContext = {
  type: "header" | "content";
  searchQuery?: string;
};

export const isActiveQuery = (
  query: Query,
  environment: PreviewQueryRequestEnvironmentEnum,
) => {
  return (
    query.environment_settings[environment].status === FeatureQueryStatus.ACTIVE
  );
};
